import React, { useState, useEffect } from "react";
import { Button, Form, Modal } from "react-bootstrap";

interface AddBrokerModalProps {
  show: boolean;
  onHide: () => void;
  selectedBroker: string;
  setSelectedBroker: (broker: string) => void;
  handleAddBroker: (assetMatches: any) => void;
  brokersList: Array<{ name: string; assets: string }>;
  existingBrokers: Array<{ name: string }>;
  isLoading?: boolean;
}

const AddBrokerModal: React.FC<AddBrokerModalProps> = ({
  show,
  onHide,
  selectedBroker,
  setSelectedBroker,
  handleAddBroker,
  brokersList,
  existingBrokers = [],
  isLoading = false,
}) => {
  // Filter out brokers that already exist
  const availableBrokers = brokersList.filter(
    (broker) => !existingBrokers.some(
      (existing) => existing.name.toLowerCase() === broker.name.toLowerCase()
    )
  );

  // Reset selected broker if it's already been added
  useEffect(() => {
    if (selectedBroker && existingBrokers.some(
      (existing) => existing.name.toLowerCase() === selectedBroker.toLowerCase()
    )) {
      setSelectedBroker("");
    }
  }, [existingBrokers, selectedBroker, setSelectedBroker]);

  // Clear username/password when switching away from Tradelocker or Binance
  useEffect(() => {
    if (selectedBroker?.toLowerCase() !== "tradelocker" && selectedBroker?.toLowerCase() !== "binance") {
      setUsername("");
      setPassword("");
    }
  }, [selectedBroker]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = () => {
    const assetMatches = {
      assetClasses: {},
      symbols: {},
      username,
      password,
    };
    handleAddBroker(assetMatches);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Broker</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-4">
          <Form.Label>Select Broker</Form.Label>
          <Form.Select
            value={selectedBroker}
            onChange={(e) => setSelectedBroker(e.target.value)}
            disabled={availableBrokers.length === 0}
          >
            <option value="">
              {availableBrokers.length === 0
                ? "All brokers have been added"
                : "Choose a broker..."}
            </option>
            {availableBrokers.map((broker) => (
              <option key={broker.name} value={broker.name}>
                {broker.name} - {broker.assets}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Username/Password fields - Only show for Tradelocker and Binance */}
        {(selectedBroker?.toLowerCase() === "tradelocker" || selectedBroker?.toLowerCase() === "binance") && (
          <>
            <Form.Group className="mb-4">
              <Form.Label>
                {selectedBroker?.toLowerCase() === "binance" ? "Sub-Account Name (Optional)" : "Username / Login"}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={selectedBroker?.toLowerCase() === "binance" ? "Enter sub-account name" : "Enter your broker username or login"}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Form.Group>

            {selectedBroker?.toLowerCase() !== "binance" && (
              <Form.Group className="mb-4">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter your broker password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            availableBrokers.length === 0 ||
            !selectedBroker ||
            (selectedBroker?.toLowerCase() === "tradelocker" && (!username || !password)) ||
            isLoading
          }
        >
          {isLoading ? "Adding..." : "Add Broker"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddBrokerModal;
